import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus,
  RefreshCw,
  FileQuestion,
  FileText,
  MoreHorizontal,
  Pencil,
  Trash2,
  Clock,
  Mic,
  CheckCircle2,
  AlertCircle,
  Search,
  Settings,
  Upload,
  Download,
  Tag
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Question categories for interview diversity
const QUESTION_CATEGORIES = [
  "Motivation & Reasons",
  "Education Quality",
  "Personal & Cultural Expectations",
  "Fixed Question 2",
  "UK Study Choice",
  "Campus Choice",
  "Course Understanding",
  "University Choice",
  "Sector Consideration",
  "Accommodation",
  "Funding",
  "Academic History",
  "Work Plans",
  "Post-Study Plans",
  "Career Goals",
  "Visa Refusal",
  "Dependants"
];

export default function OrganizationQuestions() {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [genericQuestions, setGenericQuestions] = useState([]);
  const [settings, setSettings] = useState({ use_own_questions: false });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('own'); // 'own' or 'generic'
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  const emptyQuestion = {
    title: '',
    question_text: '',
    category: '',
    keywords: [],
    expected_points: [],
    weight: 1,
    listening_time: 30,
    answering_time: 90,
    status: 'active'
  };
  
  const [formData, setFormData] = useState(emptyQuestion);
  const [keywordInput, setKeywordInput] = useState('');
  const [expectedPointInput, setExpectedPointInput] = useState('');
  const [importFile, setImportFile] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/organization/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuestions(response.data.org_questions || []);
      setGenericQuestions(response.data.generic_questions || []);
      setSettings(response.data.settings || { use_own_questions: false });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load question bank');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/api/organization/questions/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowImportDialog(false);
      setImportFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import questions');
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleJSON = () => {
    const sample = {
      questions: [
        {
          title: "Sample Question 1",
          question_text: "Why did you choose this university for your studies?",
          category: "University Choice",
          keywords: ["motivation", "university", "choice"],
          expected_points: ["Research quality", "Course content", "Location"],
          weight: 1,
          listening_time: 30,
          answering_time: 90,
          status: "active"
        },
        {
          title: "Sample Question 2",
          question_text: "What are your career goals after graduation?",
          category: "Career Goals",
          keywords: ["career", "goals", "future"],
          expected_points: ["Clear goals", "Relevance to course", "Realistic timeline"],
          weight: 1,
          listening_time: 30,
          answering_time: 90,
          status: "active"
        },
        {
          title: "Sample Question 3",
          question_text: "How do you plan to fund your studies?",
          category: "Funding",
          keywords: ["funding", "finance", "sponsorship"],
          expected_points: ["Clear funding source", "Sufficient funds", "Backup plan"],
          weight: 1,
          listening_time: 30,
          answering_time: 90,
          status: "active"
        }
      ],
      available_categories: QUESTION_CATEGORIES
    };

    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_sample.json';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Export questions as JSON
  const handleExportQuestions = async (exportType = 'own') => {
    setExporting(true);
    try {
      const questionsToExport = exportType === 'own' ? questions : genericQuestions;
      
      // Filter by category if needed
      let filteredQuestions = questionsToExport;
      if (categoryFilter && categoryFilter !== 'all') {
        filteredQuestions = questionsToExport.filter(q => q.category === categoryFilter);
      }
      
      // Filter by search if needed
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredQuestions = filteredQuestions.filter(q => 
          q.title?.toLowerCase().includes(query) ||
          q.question_text?.toLowerCase().includes(query) ||
          q.category?.toLowerCase().includes(query)
        );
      }
      
      const exportData = {
        exported_at: new Date().toISOString(),
        total: filteredQuestions.length,
        type: exportType === 'own' ? 'organization_questions' : 'generic_questions',
        available_categories: QUESTION_CATEGORIES,
        questions: filteredQuestions.map(q => ({
          title: q.title,
          question_text: q.question_text,
          category: q.category || '',
          keywords: q.keywords || [],
          expected_points: q.expected_points || [],
          weight: q.weight || 1,
          listening_time: q.listening_time || 30,
          answering_time: q.answering_time || 90,
          status: q.status || 'active'
        }))
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_${exportType}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${filteredQuestions.length} questions`);
    } catch (error) {
      toast.error('Failed to export questions');
    } finally {
      setExporting(false);
    }
  };

  // Export as CSV
  const handleExportCSV = (exportType = 'own') => {
    setExporting(true);
    try {
      const questionsToExport = exportType === 'own' ? questions : genericQuestions;
      
      // Filter by category if needed
      let filteredQuestions = questionsToExport;
      if (categoryFilter && categoryFilter !== 'all') {
        filteredQuestions = questionsToExport.filter(q => q.category === categoryFilter);
      }
      
      // Create CSV headers
      const headers = ['Title', 'Question Text', 'Category', 'Keywords', 'Expected Points', 'Weight', 'Listening Time', 'Answering Time', 'Status'];
      
      // Create CSV rows
      const rows = filteredQuestions.map(q => [
        `"${(q.title || '').replace(/"/g, '""')}"`,
        `"${(q.question_text || '').replace(/"/g, '""')}"`,
        `"${(q.category || '').replace(/"/g, '""')}"`,
        `"${(q.keywords || []).join(', ').replace(/"/g, '""')}"`,
        `"${(q.expected_points || []).join(', ').replace(/"/g, '""')}"`,
        q.weight || 1,
        q.listening_time || 30,
        q.answering_time || 90,
        q.status || 'active'
      ]);
      
      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `questions_${exportType}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported ${filteredQuestions.length} questions as CSV`);
    } catch (error) {
      toast.error('Failed to export questions');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/organization/questions`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Question created successfully!');
      setShowCreateDialog(false);
      setFormData(emptyQuestion);
      setKeywordInput('');
      setExpectedPointInput('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();
    
    if (!formData.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.put(`${API}/api/organization/questions/${selectedQuestion.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Question updated successfully!');
      setShowEditDialog(false);
      setSelectedQuestion(null);
      setFormData(emptyQuestion);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      setSubmitting(true);
      await axios.delete(`${API}/api/organization/questions/${selectedQuestion.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Question deleted successfully!');
      setShowDeleteDialog(false);
      setSelectedQuestion(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete question');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      setSubmitting(true);
      await axios.delete(`${API}/api/organization/questions/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: selectedIds
      });
      
      toast.success(`${selectedIds.length} questions deleted!`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete questions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleUseOwnQuestions = async (checked) => {
    try {
      await axios.post(`${API}/api/organization/question-settings?use_own_questions=${checked}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSettings({ ...settings, use_own_questions: checked });
      toast.success(checked 
        ? 'Your question bank is now active! New interviews will use your questions.' 
        : 'Switched back to generic questions for new interviews.'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    }
  };

  const openEditDialog = (question) => {
    setSelectedQuestion(question);
    setFormData({
      title: question.title || '',
      question_text: question.question_text,
      category: question.category || '',
      keywords: question.keywords || [],
      expected_points: question.expected_points || [],
      weight: question.weight || 1,
      listening_time: question.listening_time || 30,
      answering_time: question.answering_time || 90,
      status: question.status || 'active'
    });
    setShowEditDialog(true);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({ ...formData, keywords: [...formData.keywords, keywordInput.trim()] });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData({ ...formData, keywords: formData.keywords.filter(k => k !== keyword) });
  };

  const addExpectedPoint = () => {
    if (expectedPointInput.trim() && !formData.expected_points.includes(expectedPointInput.trim())) {
      setFormData({ ...formData, expected_points: [...formData.expected_points, expectedPointInput.trim()] });
      setExpectedPointInput('');
    }
  };

  const removeExpectedPoint = (point) => {
    setFormData({ ...formData, expected_points: formData.expected_points.filter(p => p !== point) });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredQuestions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQuestions.map(q => q.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.question_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.keywords?.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <DashboardLayout role="organization">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  // Render form fields - shared between create and edit
  const renderQuestionFormFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title (Optional)</Label>
          <Input
            id="title"
            placeholder="e.g., Why this university?"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">
            <Tag className="w-4 h-4 inline mr-1" />
            Category *
          </Label>
          <Select
            value={formData.category || "none"}
            onValueChange={(value) => setFormData(prev => ({ ...prev, category: value === "none" ? "" : value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select a category</SelectItem>
              {QUESTION_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="question_text">Question Text *</Label>
        <Textarea
          id="question_text"
          placeholder="Enter the interview question..."
          value={formData.question_text}
          onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="listening_time">
            <Clock className="w-4 h-4 inline mr-1" />
            Listening Time (seconds)
          </Label>
          <Input
            id="listening_time"
            type="number"
            min="10"
            max="120"
            value={formData.listening_time}
            onChange={(e) => setFormData(prev => ({ ...prev, listening_time: parseInt(e.target.value) || 30 }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="answering_time">
            <Mic className="w-4 h-4 inline mr-1" />
            Answering Time (seconds)
          </Label>
          <Input
            id="answering_time"
            type="number"
            min="30"
            max="300"
            value={formData.answering_time}
            onChange={(e) => setFormData(prev => ({ ...prev, answering_time: parseInt(e.target.value) || 90 }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Keywords</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Add a keyword..."
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
          />
          <Button type="button" variant="outline" onClick={addKeyword}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.keywords.map((keyword, i) => (
            <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeKeyword(keyword)}>
              {keyword} ×
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Expected Points</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Add an expected point..."
            value={expectedPointInput}
            onChange={(e) => setExpectedPointInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExpectedPoint())}
          />
          <Button type="button" variant="outline" onClick={addExpectedPoint}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.expected_points.map((point, i) => (
            <Badge key={i} variant="outline" className="cursor-pointer" onClick={() => removeExpectedPoint(point)}>
              {point} ×
            </Badge>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <DashboardLayout role="organization">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Question Bank</h2>
            <p className="text-muted-foreground">Create and manage your organization's interview questions</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting} data-testid="export-questions-btn">
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExportQuestions('own')}>
                  <FileQuestion className="w-4 h-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportCSV('own')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              data-testid="import-questions-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Questions
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowSettingsDialog(true)}
              data-testid="question-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              onClick={() => {
                setFormData(emptyQuestion);
                setKeywordInput('');
                setExpectedPointInput('');
                setShowCreateDialog(true);
              }}
              className="bg-lime-500 hover:bg-lime-600"
              data-testid="add-question-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
          </div>
        </div>

        {/* Status Card */}
        <Card className={settings.use_own_questions ? "border-lime-500/50 bg-lime-500/5" : "border-amber-500/50 bg-amber-500/5"}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                {settings.use_own_questions ? (
                  <CheckCircle2 className="w-5 h-5 text-lime-500 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`font-medium ${settings.use_own_questions ? 'text-lime-600' : 'text-amber-600'}`}>
                    {settings.use_own_questions ? 'Your Question Bank is Active' : 'Using Generic Questions'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {settings.use_own_questions 
                      ? 'New interviews for your organization will use these questions.'
                      : 'Enable your question bank to use custom questions for interviews.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.use_own_questions}
                onCheckedChange={handleToggleUseOwnQuestions}
                disabled={questions.length === 0}
              />
            </div>
          </CardContent>
        </Card>

        {/* Search and Bulk Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {selectedIds.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedIds.length} Selected
            </Button>
          )}
        </div>

        {/* Questions Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Your Questions</CardTitle>
                <CardDescription>{questions.length} questions in your bank</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredQuestions.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedIds.length === filteredQuestions.length && filteredQuestions.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Question</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Timing</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuestions.map((question) => (
                      <TableRow key={question.id} data-testid={`question-row-${question.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(question.id)}
                            onCheckedChange={() => toggleSelect(question.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md">
                            {question.title && (
                              <p className="font-medium text-sm text-lime-600">{question.title}</p>
                            )}
                            <p className="text-sm line-clamp-2">{question.question_text}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {question.category ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Tag className="w-3 h-3 mr-1" />
                              {question.category}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {question.listening_time}s listen
                            </div>
                            <div className="flex items-center gap-1">
                              <Mic className="w-3 h-3" />
                              {question.answering_time}s answer
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={question.status === 'active' ? 'default' : 'secondary'}>
                            {question.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(question)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedQuestion(question);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileQuestion className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No questions yet</p>
                <p className="text-sm mt-2">Create your first question to build your custom interview.</p>
                <Button 
                  className="mt-4 bg-lime-500 hover:bg-lime-600" 
                  onClick={() => {
                    setFormData(emptyQuestion);
                    setShowCreateDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Question
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Question Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5 text-lime-500" />
              Add New Question
            </DialogTitle>
            <DialogDescription>
              Create a new interview question for your organization.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateQuestion} className="space-y-4">
            {renderQuestionFormFields()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-lime-500 hover:bg-lime-600">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Question
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-lime-500" />
              Edit Question
            </DialogTitle>
            <DialogDescription>
              Update your interview question.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditQuestion} className="space-y-4">
            {renderQuestionFormFields()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-lime-500 hover:bg-lime-600">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Question</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedQuestion && (
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <p className="line-clamp-2">{selectedQuestion.question_text}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteQuestion} disabled={submitting}>
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-lime-500" />
              Question Bank Settings
            </DialogTitle>
            <DialogDescription>
              Configure how your question bank is used in interviews.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Use My Question Bank</p>
                <p className="text-sm text-muted-foreground">
                  When enabled, interviews for your organization will use your custom questions instead of generic ones.
                </p>
              </div>
              <Switch
                checked={settings.use_own_questions}
                onCheckedChange={handleToggleUseOwnQuestions}
                disabled={questions.length === 0}
              />
            </div>
            
            {questions.length === 0 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-sm text-amber-600">
                  You need to create at least one question before enabling your question bank.
                </p>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground">
                <strong>Current Status:</strong> {settings.use_own_questions 
                  ? `Using ${questions.length} custom questions`
                  : 'Using generic questions from Super Admin'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowSettingsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-lime-500" />
              Import Questions
            </DialogTitle>
            <DialogDescription>
              Upload a JSON file to bulk import questions to your organization's question bank.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
              <Input
                type="file"
                accept=".json"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="hidden"
                id="import-file"
              />
              <label htmlFor="import-file" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {importFile ? importFile.name : 'Click to select a JSON file'}
                </p>
              </label>
            </div>

            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-2">JSON Format:</p>
              <pre className="bg-slate-100 dark:bg-slate-900 p-2 rounded text-xs overflow-x-auto">
{`{
  "questions": [
    {
      "title": "Question Title",
      "question_text": "Your question?",
      "keywords": ["key1", "key2"],
      "expected_points": ["point1"],
      "weight": 1,
      "listening_time": 30,
      "answering_time": 90,
      "status": "active"
    }
  ]
}`}
              </pre>
            </div>

            <Button 
              variant="outline" 
              onClick={downloadSampleJSON}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Sample JSON
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setImportFile(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-lime-500 hover:bg-lime-600"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import Questions'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
